import Heading from '@/components/layout/Heading';
import { Button } from '@/components/ui/shadcn/button';
import { Separator } from '@/components/ui/shadcn/separator';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, useCan } from '@/lib/utils';
import { type NavItem } from '@/types';
import { Link } from '@inertiajs/react';
import {
    Briefcase,
    Building2,
    CalendarDays,
    Cloud,
    Compass,
    Database,
    Mail,
    Settings,
    Shield,
    ShoppingCart,
    Ticket,
    Users,
    Wrench,
} from 'lucide-react';
import { type PropsWithChildren } from 'react';

const sidebarNavItems: NavItem[] = [
    {
        title: 'Profile',
        url: '/settings/profile',
        icon: null,
    },
    {
        title: 'Password',
        url: '/settings/password',
        icon: null,
    },
    {
        title: 'Appearance',
        url: '/settings/appearance',
        icon: null,
    },
    {
        title: '__SEPARATOR__',
        url: '',
        icon: null,
        permission: 'settings.manage',
    },
    {
        title: 'General',
        url: '/settings/system/general',
        icon: Settings,
        permission: 'settings.manage',
    },
    {
        title: 'Navigation',
        url: '/settings/system/navigation',
        icon: Compass,
        permission: 'settings.manage',
    },
    {
        title: 'Companies',
        url: '/settings/system/companies',
        icon: Building2,
        permission: 'settings.manage',
    },
    {
        title: 'Employees',
        url: '/settings/system/employees',
        icon: Users,
        permission: 'settings.manage',
    },
    {
        title: 'CRM',
        url: '/settings/system/crm',
        icon: Users,
        permission: 'settings.manage',
    },
    {
        title: 'Projects',
        url: '/settings/system/projects',
        icon: Briefcase,
        permission: 'settings.manage',
    },
    {
        title: 'Work Orders',
        url: '/settings/system/work-orders',
        icon: Wrench,
        permission: 'settings.manage',
    },
    {
        title: 'Vacations',
        url: '/settings/vacations',
        icon: CalendarDays,
        permission: 'vacations.manage',
    },
    {
        title: 'Email Templates',
        translationKey: 'email_templates.nav',
        url: '/settings/email-templates',
        icon: Mail,
        permission: 'settings.manage',
    },
    {
        title: 'Data Import',
        url: '/settings/system/data-import',
        icon: Database,
        permission: 'settings.manage',
    },
    {
        title: 'Security',
        url: '/settings/system/security',
        icon: Shield,
        permission: 'settings.manage',
    },
    {
        title: 'Service Requests',
        url: '/settings/system/service-requests',
        icon: Ticket,
        permission: 'settings.manage',
    },
    {
        title: 'Purchase Orders',
        url: '/settings/system/purchase-orders',
        icon: ShoppingCart,
        permission: 'settings.manage',
    },
    {
        title: 'SharePoint',
        url: '/settings/integrations/sharepoint',
        icon: Cloud,
        permission: 'settings.sharepoint.manage',
    },
];

export default function SettingsLayout({ children }: PropsWithChildren) {
    const { can } = useCan();
    const { t } = useTranslations();

    // When server-side rendering, we only render the layout on the client...
    if (typeof window === 'undefined') {
        return null;
    }

    const currentPath = window.location.pathname;

    const isItemActive = (item: NavItem): boolean => {
        if (currentPath === item.url) return true;
        return false;
    };

    const renderNavItem = (item: NavItem) => {
        // Check permission if specified
        if (item.permission && !can(item.permission)) {
            return null;
        }

        // Render separator
        if (item.title === '__SEPARATOR__') {
            return <Separator key="separator" className="my-2" />;
        }

        const isActive = isItemActive(item);

        return (
            <Button
                key={item.url}
                size="sm"
                variant="ghost"
                asChild
                className={cn('w-full justify-start', {
                    'bg-muted': isActive,
                })}
            >
                <Link href={item.url} prefetch>
                    {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                    {item.translationKey ? t(item.translationKey, item.title) : item.title}
                </Link>
            </Button>
        );
    };

    return (
        <div className="px-4 py-6">
            <Heading title="Settings" description="Manage your profile and account settings" />

            <div className="flex flex-col space-y-8 lg:flex-row lg:space-y-0 lg:space-x-12">
                <aside className="w-full max-w-xl lg:w-64">
                    <nav className="flex flex-col space-y-1 space-x-0">{sidebarNavItems.map((item) => renderNavItem(item))}</nav>
                </aside>

                <Separator className="my-6 md:hidden" />

                <div className="w-full flex-1">
                    <section className="max-w-full space-y-12">{children}</section>
                </div>
            </div>
        </div>
    );
}
